// Configuración data-driven de las 16 plantillas de acción de Cuenta Vaca.
// El motor (ActionRunner) lee esta config para renderizar y enviar al backend.
//
// Nombres de campo reservados (van al nivel superior del payload del backend):
//   date, farm_id, chip_code, lomo_number, to_farm_id, from_farm_id, chips
// Cualquier otro `name` se envía dentro de `payload`.

const C = {
  ingreso: "hsl(var(--c-ingreso))",
  egreso: "hsl(var(--c-egreso))",
  cria: "hsl(var(--c-cria))",
  matadero: "hsl(var(--c-matadero))",
  traslado: "hsl(var(--c-traslado))",
  evento: "hsl(var(--c-evento))",
  extravio: "hsl(var(--c-extravio))",
  amarillo: "hsl(45 75% 46%)",
};

export const CAT_COLOR = {
  ingreso: "var(--c-ingreso)",
  egreso: "var(--c-egreso)",
  cria: "var(--c-cria)",
  matadero: "var(--c-matadero)",
  traslado: "var(--c-traslado)",
  evento: "var(--c-evento)",
  extravio: "var(--c-extravio)",
};

// Sets de opciones reutilizables
const GEN = [
  { e: "🔴", t: "Hembra", v: "female", c: C.egreso },
  { e: "🔵", t: "Macho", v: "male", c: C.cria },
];
const SINO = [
  { e: "✅", t: "Sí", v: "yes", c: C.ingreso },
  { e: "⛔️", t: "No", v: "no", c: C.egreso },
];
const VERIF = [
  { e: "🟢", t: "Sí, coincide", v: "ok", c: C.ingreso },
  { e: "🔴", t: "No coincide", v: "mismatch", c: C.egreso },
];

// Diagnóstico de palpación, compartido entre el modo individual y el masivo
// (P11) — el nivel 1-9 se eliminó porque duplicaba diagnóstico + mes de preñez.
const PALPATION_DIAGNOSIS_FIELD = {
  k: "toggle", name: "diagnosis", lab: "Diagnóstico", req: 1, opts: [
    { e: "🟢", t: "Preñada", v: "pregnant", c: C.ingreso, reveal: "meses" },
    { e: "🟣", t: "Vacía", v: "empty", c: C.evento, reveal: "notav" },
    { e: "🔴", t: "Vacía descarte", v: "empty_cull", c: C.egreso, reveal: "notad" },
  ], reveals: {
    meses: { k: "num", name: "months", lab: "🟢 Meses de preñez", ph: "Ej. 2.5 = 2 meses y 15 días", unit: "m" },
    notav: { k: "area", name: "empty_note", lab: "🟣 Nota (motivo especulativo)", ph: "Motivo por el que está vacía…" },
    notad: { k: "area", name: "cull_note", lab: "🔴 Nota (motivo de descarte)", ph: "Motivo del descarte…" },
  },
};

// Campo opcional de precio para ventas (matadero y cría). Si queda vacío, el
// informe de "Ventas y despachos" funciona igual, sin mostrar montos.
const PRICE_FIELD = {
  k: "toggle", name: "price_mode", lab: "💰 Precio (opcional)", opt: 1, opts: [
    { e: "⚖️", t: "Por kilo", v: "per_kg", c: C.ingreso, reveal: "perkg" },
    { e: "💵", t: "Total", v: "total", c: C.cria, reveal: "total" },
  ], reveals: {
    perkg: { k: "num", name: "price_per_kg", lab: "💰 Precio por kilo", opt: 1, unit: "$/kg", ph: "Opcional" },
    total: { k: "num", name: "price_total", lab: "💰 Precio total", opt: 1, unit: "$", ph: "Opcional" },
  },
};

export const TEMPLATES = {
  // --------------------------- INGRESOS ---------------------------
  1: {
    id: 1, cat: "ingreso", ico: "🔆➕", title: "Nacimiento", sub: "Registrar un parto",
    num: "PLANTILLA 1", navTo: "/animales/nacimiento",
  },
  2: {
    id: 2, cat: "ingreso", ico: "🛒➕", title: "Compra", sub: "Animal comprado",
    num: "PLANTILLA 2", actionType: "purchase", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha de recepción", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️ Finca de ingreso", req: 1 },
      { k: "text", name: "source", lab: "📍🌐 Lugar de procedencia", req: 1, ph: "Hacienda / vendedor de origen" },
      { k: "text", name: "chip_code", lab: "#️⃣💉 Chip asignado", req: 1, ph: "15 dígitos", scan: 1, mustBeFree: 1 },
      { k: "text", name: "lomo_number", lab: "#️⃣ Número de lomo", ph: "Ej. 0470" },
      { k: "text", name: "iron_brand", lab: "#️⃣ Hierro pierna / paleta", ph: "Marca de hierro" },
      { k: "text", name: "birth_year", lab: "📅 Año de nacimiento", ph: "Ej. 2023" },
      { k: "text", name: "ear_tattoo_left", lab: "🐮➡️ Tatuaje oreja izquierda", opt: 1, ph: "Opcional" },
      { k: "text", name: "ear_tattoo_right", lab: "⬅️🐮 Tatuaje oreja derecha", opt: 1, ph: "Opcional" },
      { k: "toggle", name: "sex", lab: "Género", req: 1, opts: GEN },
      { k: "num", name: "weight_kg", lab: "⚖️ Peso", opt: 1, unit: "kg", ph: "Opcional" },
      { k: "area", name: "notes", lab: "📝 Notas", ph: "Vendedor, precio, condiciones…" },
      { k: "accept", lab: "Registrar compra" },
    ],
  },
  // --------------------------- EGRESOS ---------------------------
  3: {
    id: 3, cat: "egreso", ico: "➖☠️", title: "Muerte", sub: "Baja por muerte",
    num: "PLANTILLA 3", actionType: "death", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆☠️ Fecha de muerte", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️ Lugar de muerte" },
      { k: "text", name: "chip_code", lab: "#️⃣ Chip", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "text", name: "lomo_number", lab: "#️⃣ Lomo", ph: "4 o 6 dígitos" },
      { k: "text", name: "ear_right", lab: "🐮➡️ Oreja derecha", ph: "Tatuaje / arete" },
      { k: "area", name: "notes", lab: "📝 Notas", req: 1, ph: "Causa de muerte…" },
      { k: "accept", lab: "Registrar muerte" },
    ],
  },
  4: {
    id: 4, cat: "egreso", ico: "🔪🥩", title: "Sacrificio", sub: "Faena en finca",
    num: "PLANTILLA 4", actionType: "slaughter", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆🔪 Fecha de sacrificio", req: 1, def: "today" },
      { k: "text", name: "chip_code", lab: "#️⃣ Chip del animal", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "toggle", name: "verification", lab: "Verificación de datos", opts: VERIF },
      { k: "num", name: "weight_kg", lab: "⚖️ Peso", unit: "kg", ph: "Peso en canal / vivo" },
      { k: "area", name: "notes", lab: "📝 Notas", ph: "Autorizado por… y motivo del sacrificio" },
      { k: "accept", lab: "Registrar sacrificio" },
    ],
  },
  5: {
    id: 5, cat: "cria", ico: "🔹", title: "Venta para cría", sub: "Traslado a comprador",
    num: "PLANTILLA 5", actionType: "sale_breeding", successTo: "hub",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha", req: 1, def: "today" },
      { k: "text", name: "destination", lab: "⛳️🌐 Finca a donde va", req: 1, ph: "Finca / comprador destino", note: "Debe estar creada por el dueño" },
      { k: "text", name: "guide_number", lab: "📄 Número de Guía INSAI", req: 1, ph: "N° de movilización" },
      { k: "date", name: "guide_date", lab: "📆 Fecha de la guía" },
      { k: "text", name: "buyer", lab: "👤 Comprador", ph: "Nombre del comprador" },
      { k: "text", name: "dest_farm_name", lab: "🏡 Finca destino", ph: "Nombre de la finca destino" },
      { k: "text", name: "address", lab: "📍 Dirección", ph: "Dirección destino" },
      { k: "text", name: "driver", lab: "🚚 Chofer", ph: "Nombre del chofer" },
      { k: "text", name: "driver_id", lab: "🪪 Cédula", ph: "C.I. del chofer" },
      { k: "text", name: "plate", lab: "🔢 Placa", ph: "Placa del vehículo" },
      PRICE_FIELD,
      { k: "chips", name: "chips", lab: "#️⃣ Chips de los animales", variant: "both", total: "count" },
      { k: "accept", lab: "Aceptar venta para cría" },
    ],
  },
  6: {
    id: 6, cat: "matadero", ico: "🔶", title: "Venta a matadero", sub: "Despacho a faena",
    num: "PLANTILLA 6", actionType: "sale_slaughterhouse", successTo: "hub",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha", req: 1, def: "today" },
      { k: "text", name: "matadero_name", lab: "⛳️🌐 Nombre del matadero", req: 1, ph: "Nombre del matadero", note: "Debe estar creado por el dueño" },
      { k: "text", name: "address", lab: "📍 Dirección", ph: "Dirección del matadero" },
      { k: "text", name: "guide_number", lab: "📄 Número de Guía INSAI", req: 1, ph: "N° de movilización" },
      { k: "date", name: "guide_date", lab: "📆 Fecha guía INSAI" },
      { k: "text", name: "driver", lab: "🚚 Chofer", ph: "Nombre del chofer" },
      { k: "text", name: "driver_id", lab: "🪪 Cédula", ph: "C.I. del chofer" },
      { k: "text", name: "plate", lab: "🔢 Placa", ph: "Placa del vehículo" },
      PRICE_FIELD,
      { k: "chips", name: "chips", lab: "#️⃣ Animales despachados (chip + peso)", variant: "both", total: "weight" },
      { k: "accept", lab: "Aceptar despacho a matadero" },
    ],
  },
  7: {
    id: 7, cat: "extravio", ico: "⛔️", title: "Declarada extraviada", sub: "Solo dueño",
    num: "PLANTILLA 7", actionType: "lost", ownerOnly: 1, successTo: "animal",
    fields: [
      { k: "note", tone: "owner", ni: "🔐", txt: "Solo el Dueño puede generar y ver esta plantilla, tras varios inventarios sin que el animal aparezca (robo evidente)." },
      { k: "text", name: "chip_code", lab: "#️⃣ Número de chip", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "text", name: "lomo_number", lab: "#️⃣ Número de lomo", ph: "4 o 6 dígitos" },
      { k: "area", name: "notes", lab: "📝 Notas", ph: "Asunto, resultados de la búsqueda…" },
      { k: "toggle", name: "determination", lab: "Se determinó", opts: [
        { e: "🔴", t: "Robada", v: "stolen", c: C.egreso },
        { e: "⚪️", t: "Extravío", v: "lost", c: C.extravio },
      ] },
      { k: "text", name: "responsible", lab: "🧑‍🌾 Bajo responsabilidad de", req: 1, ph: "Encargado responsable", note: "Se genera expediente por finca y responsable" },
      { k: "accept", lab: "Generar expediente" },
    ],
  },
  // --------------------------- TRASLADOS ---------------------------
  8: {
    id: 8, cat: "traslado", ico: "🚚↗️", title: "Salida de traslado", sub: "A otra finca propia",
    num: "PLANTILLA 8", actionType: "transfer_out", successTo: "hub",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha", req: 1, def: "today" },
      { k: "destfarm", name: "to_farm_id", lab: "⛳️🌐 Finca a donde va", req: 1, note: "Debe estar creada por el dueño" },
      { k: "text", name: "driver", lab: "🚚 Chofer", ph: "Nombre del chofer" },
      { k: "text", name: "driver_id", lab: "🪪 Cédula", ph: "C.I. del chofer" },
      { k: "text", name: "plate", lab: "🔢 Placa", ph: "Placa del vehículo" },
      { k: "chips", name: "chips", lab: "#️⃣ Chips de los animales", variant: "both", total: "count" },
      { k: "accept", lab: "Aceptar salida de traslado" },
    ],
  },
  9: {
    id: 9, cat: "traslado", ico: "🚛↙️", title: "Recibo de traslado", sub: "Recibir animales",
    num: "PLANTILLA 9", actionType: "transfer_in", successTo: "hub",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛳️🌐 Finca que recibe", req: 1 },
      { k: "originfarm", name: "from_farm_id", lab: "🚛 Finca de donde viene", opt: 1 },
      { k: "chips", name: "chips", lab: "#️⃣ Lectura de chips (carga masiva)", variant: "mass", total: "count" },
      { k: "toggle", name: "verification", lab: "Verificación de datos", opts: VERIF },
      { k: "num", name: "arrival_weight", lab: "⚖️ Peso al llegar", opt: 1, unit: "kg", ph: "Opcional" },
      { k: "area", name: "notes", lab: "📝 Notas", ph: "Chofer, placa, otros…" },
      { k: "accept", lab: "Aceptar recibo" },
    ],
  },
  // --------------------------- EVENTOS ---------------------------
  10: {
    id: 10, cat: "evento", ico: "🐄🔻☠️", title: "Aborto", sub: "Registro de evento",
    num: "PLANTILLA 10", actionType: "abortion", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆🔻 Fecha de aborto", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️ Lugar del aborto" },
      { k: "text", name: "chip_code", lab: "#️⃣ Chip de la vaca", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "text", name: "lomo_number", lab: "#️⃣ Lomo de la vaca", opt: 1, ph: "Opcional, si no se lee el chip" },
      { k: "toggle", name: "fetus_sex", lab: "🐣 Género del feto", opts: GEN },
      { k: "subhead", txt: "Preguntas sobre el feto" },
      { k: "q", name: "fetus_hair", lab: "¿Tiene pelo?", opts: SINO },
      { k: "q", name: "fetus_features", lab: "¿Tiene orejas / cola / hocico?", opts: SINO },
      { k: "q", name: "fetus_fine_hair", lab: "¿Pelo fino en todo el cuerpo, incisivos presentes sin erupcionar?", opts: SINO },
      { k: "q", name: "fetus_full_hair", lab: "¿Pelo completo / incisivos erupcionados?", opts: SINO },
      { k: "subhead", txt: "Preguntas sobre la vaca" },
      { k: "q", name: "placenta_found", lab: "¿Se encontró la placenta?", opts: SINO },
      { k: "q", name: "placenta_kept", lab: "¿Se guardó?", opts: SINO },
      { k: "q", name: "placenta_retained", lab: "¿Tiene placenta retenida?", opts: SINO },
      { k: "q", name: "placenta_thick", lab: "¿Placenta gruesa?", opts: SINO },
      { k: "q", name: "necrosis", lab: "¿Necrosis o mal olor?", opts: SINO },
      { k: "q", name: "pus", lab: "¿Pus presente?", opts: SINO },
      { k: "toggle", name: "placenta_color", lab: "Color de la placenta", opts: [
        { e: "🟨", t: "Amarillo", v: "yellow", c: C.amarillo },
        { e: "⬜️", t: "Gris", v: "gray", c: C.extravio },
      ] },
      { k: "accept", lab: "Registrar aborto" },
    ],
  },
  11: {
    id: 11, cat: "evento", ico: "📈🐮", title: "Palpación veterinaria", sub: "Registro de evento",
    num: "PLANTILLA 11", actionType: "palpation", bulkActionType: "palpation_bulk", ownerOnly: 1, successTo: "animal",
    fields: [
      { k: "note", tone: "owner", ni: "🩺", txt: "Solo se llena desde el perfil del Veterinario o del Dueño." },
      { k: "text", name: "vet", lab: "👨‍⚕️ Veterinario", ph: "Nombre del veterinario" },
      { k: "text", name: "chip_code", lab: "#️⃣🐄 Chip", req: 1, ph: "Escanear chip", scan: 1, lookup: 1 },
      { k: "text", name: "lomo_number", lab: "#️⃣🐄 Lomo", ph: "Aparece al leer el chip" },
      PALPATION_DIAGNOSIS_FIELD,
      { k: "accept", lab: "Guardar palpación" },
    ],
    bulkFields: [
      { k: "note", tone: "owner", ni: "🩺", txt: "Cargá el diagnóstico una sola vez y aplicalo a toda la lista de vacas (ej. 15 animales con 2 meses de preñez)." },
      { k: "text", name: "vet", lab: "👨‍⚕️ Veterinario", ph: "Nombre del veterinario" },
      PALPATION_DIAGNOSIS_FIELD,
      { k: "chips", name: "chips", lab: "#️⃣🐄 Chips de las vacas", variant: "both", total: "count" },
      { k: "accept", lab: "Guardar palpaciones" },
    ],
  },
  12: {
    id: 12, cat: "evento", ico: "🥛🐄", title: "Ingreso a ordeño", sub: "Registro de evento",
    num: "PLANTILLA 12", actionType: "milking_in", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha inicio de ordeño", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️🥛 Lugar de ordeño", req: 1 },
      { k: "text", name: "chip_code", lab: "#️⃣🐄 Chip de la vaca", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "num", name: "weight_kg", lab: "⚖️ Peso al ingresar", unit: "kg", ph: "Ej. 480" },
      { k: "area", name: "notes", lab: "📝 Notas", ph: "Observaciones…" },
      { k: "note", tone: "alarm", ni: "⏰", txt: "Se creará una alarma de ciclo completado al cumplir 200 días desde el ingreso." },
      { k: "accept", lab: "Registrar ingreso a ordeño" },
    ],
  },
  13: {
    id: 13, cat: "evento", ico: "📈🥛", title: "Producción de leche", sub: "Registro de evento",
    num: "PLANTILLA 13", actionType: "milk_production", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha del registro", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️🥛 Lugar de ordeño", req: 1 },
      { k: "text", name: "chip_code", lab: "#️⃣🐄 Chip de la vaca", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "num", name: "quantity", lab: "🥛 Cantidad producida", unit: "Lts/kg", ph: "3 dígitos · ej. 12" },
      { k: "area", name: "notes", lab: "📝 Notas", opt: 1, ph: "Opcional" },
      { k: "accept", lab: "Registrar producción" },
    ],
  },
  14: {
    id: 14, cat: "evento", ico: "🐮➡️", title: "Salida de ordeño (secado)", sub: "Registro de evento",
    num: "PLANTILLA 14", actionType: "drying", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha inicio de secado", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛺️🥛 Lugar de ordeño", req: 1 },
      { k: "text", name: "chip_code", lab: "#️⃣🐄 Chip de la vaca", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "num", name: "liters_before", lab: "🥛 Litros antes del secado", unit: "Lts/kg", ph: "3 dígitos" },
      { k: "note", tone: "info", ni: "⏰", txt: "Conteo automático de días en secado · seguí registrando fecha y peso de leche." },
      { k: "note", tone: "warn", ni: "‼️", txt: "El secado no debe pasar de 30 días." },
      { k: "subhead", txt: "Manejo del secado" },
      { k: "q", name: "with_calf", lab: "¿Se soltó junto al becerro?", opts: SINO },
      { k: "q", name: "syringe", lab: "¿Se usó jeringa o cánula de secado?", opts: SINO, reveal: "syringe_note" },
      { k: "text", name: "syringe_note", lab: "Nota: producto usado", ph: "Ej. Cepravin, Secamil", hidden: 1 },
      { k: "q", name: "injected", lab: "¿Tratamiento inyectado para secado?", opts: SINO, reveal: "injected_note" },
      { k: "text", name: "injected_note", lab: "Nota: tratamiento inyectado", ph: "Ej. Velactis (inyección única, inhibe la hormona de la leche)", hidden: 1 },
      { k: "accept", lab: "Registrar secado" },
    ],
  },
  15: {
    id: 15, cat: "evento", ico: "🏖️", title: "Ingreso a escotero", sub: "Registro de evento",
    num: "PLANTILLA 15", actionType: "escotero", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha de ingreso a escotero", req: 1, def: "today" },
      { k: "text", name: "chip_code", lab: "#️⃣🐄 Chip de la vaca", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "num", name: "weight_kg", lab: "⚖️ Peso al ingresar", unit: "kg", ph: "Ej. 420" },
      { k: "area", name: "notes", lab: "📝 Nota", opt: 1, ph: "Opcional" },
      { k: "accept", lab: "Registrar ingreso a escotero" },
    ],
  },
  16: {
    id: 16, cat: "evento", ico: "📊", title: "Destete", sub: "Registro de evento",
    num: "PLANTILLA 16", actionType: "weaning", successTo: "animal",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha de destete", req: 1, def: "today" },
      { k: "text", name: "chip_code", lab: "#️⃣ Chip del becerro", req: 1, ph: "15 dígitos", scan: 1, lookup: 1 },
      { k: "num", name: "weight_kg", lab: "⚖️ Peso al destete", unit: "kg", ph: "2 dígitos" },
      { k: "accept", lab: "Registrar destete" },
    ],
  },
  // Fix Q (2026-10-21): mover uno o muchos animales de un lote a otro de la misma finca.
  17: {
    id: 17, cat: "traslado", ico: "🔀🐄", title: "Cambio de lote", sub: "Mover animales entre lotes",
    num: "PLANTILLA 17", actionType: "lot_change", successTo: "hub",
    fields: [
      { k: "date", name: "date", lab: "📆 Fecha", req: 1, def: "today" },
      { k: "place", name: "farm_id", lab: "⛳️ Finca", req: 1 },
      { k: "fromlot", name: "from_lot_id", lab: "📍 Lote de origen", opt: 1, fills: "chips", note: "Opcional: al elegirlo se cargan todos sus animales activos y podés quitar los que no se mueven." },
      { k: "lot", name: "to_lot_id", lab: "🎯 Lote de destino", req: 1 },
      { k: "chips", name: "chips", lab: "#️⃣🐄 Chips de los animales", variant: "both", total: "count" },
      { k: "area", name: "notes", lab: "📝 Nota", opt: 1, ph: "Opcional" },
      { k: "accept", lab: "Mover animales" },
    ],
  },
};

// Agrupación del hub (orden y color de cada grupo)
export const MENU = [
  { key: "ingreso", label: "Ingresan a inventario", color: "var(--c-ingreso)", items: [1, 2] },
  { key: "egreso", label: "Egresan de inventario", color: "var(--c-egreso)", items: [3, 4, 5, 6, 7] },
  { key: "traslado", label: "Traslados y cambios de lote", color: "var(--c-traslado)", items: [8, 9, 17] },
  { key: "evento", label: "Registros de evento", note: "no modifican inventario", color: "var(--c-evento)", items: [10, 11, 12, 13, 14, 15, 16] },
];

// Campos que se mapean al nivel superior del payload del backend
export const RESERVED_TOP = ["date", "farm_id", "chip_code", "lomo_number", "to_farm_id", "from_farm_id", "chips"];

export function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}
